import { useMutation } from "@apollo/client";
import { AlertCircle, Loader2 } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimplePhoneInput } from "@/components/ui/simple-phone-input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { UPDATE_CUSTOMER_MARKETING_CONSENT } from "@/domains/customer/consent/graphql/consent-queries";
import type { BulkCustomerPhoneInput } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import {
  useCreateCustomerSilent,
  useUpdateCustomerSilent,
} from "../../hooks/use-customer-mutation";

interface CustomerManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerId?: string | null;
  onSave?: () => void;
}

interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phones: BulkCustomerPhoneInput[];
  dateOfBirth?: string;
  notes: string;
  marketingConsent: boolean;
}

export function CustomerCreateDialog({
  isOpen,
  onClose,
  customerId,
  onSave,
}: CustomerManagementDialogProps) {
  const { t } = useTranslation("customers");
  const { currentSalon } = useSalonStore();
  const {
    execute: createCustomer,
    isLoading: isCreating,
    error: createError,
  } = useCreateCustomerSilent();
  const {
    execute: updateCustomer,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateCustomerSilent();
  const [updateMarketingConsentMutation, { loading: isUpdatingConsent }] = useMutation(
    UPDATE_CUSTOMER_MARKETING_CONSENT
  );
  const [formData, setFormData] = useState<CustomerFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phones: [],
    dateOfBirth: "",
    notes: "",
    marketingConsent: false,
  });
  const isSaving = isCreating || isUpdating || isUpdatingConsent;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dateOfBirthDisplay, setDateOfBirthDisplay] = useState("");

  const isEditing = !!customerId;

  const parseDateFromDisplay = (displayDate: string) => {
    if (!displayDate) return "";
    // Parse dd/MM/yy format
    const parsed = DateTime.fromFormat(displayDate, "dd/MM/yy");
    if (parsed.isValid) {
      return parsed.toISODate();
    }
    return "";
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      // TODO: If editing, load customer data here
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phones: [],
        dateOfBirth: "",
        notes: "",
        marketingConsent: false,
      });
      setDateOfBirthDisplay("");
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("validation.firstNameRequired");
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t("validation.lastNameRequired");
    }
    // Email validation - only validate if email is provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.validEmail");
    }
    // Phone validation removed - phone is now optional

    // Date of birth validation - only validate if date is provided
    if (dateOfBirthDisplay.trim()) {
      const parsed = DateTime.fromFormat(dateOfBirthDisplay, "dd/MM/yy");
      if (!parsed.isValid) {
        newErrors.dateOfBirth = t("form.validation.dateFormatError");
      }
    }

    setErrors(newErrors);

    // Focus first field with error
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      setTimeout(() => {
        const element = document.getElementById(
          firstErrorField === "phone-0" ? "phone-0" : firstErrorField
        );
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !currentSalon?.id) return;

    setErrors({}); // Clear all previous errors

    const input = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email.trim() || undefined,
      phones: formData.phones,
      dateOfBirth: formData.dateOfBirth || null,
      notes: formData.notes,
    };

    let result: unknown;
    if (isEditing && customerId) {
      result = await updateCustomer(currentSalon.id, customerId, input);
    } else {
      result = await createCustomer(currentSalon.id, input);
    }

    // Check if the operation was successful
    if (result) {
      // For new customers, handle marketing consent after creation
      if (!isEditing && result && typeof result === "object" && "id" in result) {
        const newCustomerId = (result as { id: string }).id;

        // Set marketing consent if the customer opted in
        if (formData.marketingConsent) {
          try {
            await updateMarketingConsentMutation({
              variables: {
                customerId: newCustomerId,
                hasMarketingConsent: true,
              },
            });
          } catch (consentError) {
            console.error("Failed to set marketing consent:", consentError);
            // Don't fail the entire operation - customer creation succeeded
            toast.warning(t("form.marketing.warningMessage"));
          }
        }
      }

      toast.success(
        isEditing ? t("form.messages.updateSuccess") : t("form.messages.createSuccess")
      );
      // Only close dialog and trigger callbacks on success
      onSave?.();
      onClose();
    } else {
      // If result is null, it means the operation failed
      // Check if there's an error in the hook's error state
      const error = isEditing ? updateError : createError;
      const errorMessage = error?.message || t("form.messages.saveError");

      // Get structured field errors from GraphQL extensions
      const graphQLError = (
        error as {
          graphQLErrors?: Array<{
            extensions?: { fieldErrors?: Record<string, string>; code?: string };
          }>;
        }
      )?.graphQLErrors?.[0];
      const fieldErrors = graphQLError?.extensions?.fieldErrors;

      // Use structured field errors from GraphQL extensions
      const finalFieldErrors = fieldErrors;

      if (finalFieldErrors && Object.keys(finalFieldErrors).length > 0) {
        setErrors(finalFieldErrors);
        // Auto-focus first error field
        const firstField = Object.keys(finalFieldErrors)[0];
        setTimeout(() => {
          const element = document.getElementById(firstField);
          if (element) {
            element.focus();
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      } else {
        setErrors({ submit: errorMessage });
      }
    }
  };

  const handleChange =
    (field: keyof CustomerFormData) => (value: string | boolean | BulkCustomerPhoneInput[]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

  const handlePhonesChange = (phones: BulkCustomerPhoneInput[]) => {
    setFormData((prev) => ({ ...prev, phones }));
    // Clear phone errors when phones change
    const phoneErrors = Object.keys(errors).filter((key) => key.startsWith("phone"));
    if (phoneErrors.length > 0) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        phoneErrors.forEach((key) => {
          delete newErrors[key];
        });
        return newErrors;
      });
    }
  };

  const handleDateOfBirthChange = (value: string) => {
    setDateOfBirthDisplay(value);
    const isoDate = parseDateFromDisplay(value);
    setFormData((prev) => ({ ...prev, dateOfBirth: isoDate }));
    // Clear date error when user starts typing
    if (errors.dateOfBirth) {
      setErrors((prev) => ({ ...prev, dateOfBirth: "" }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isEditing ? t("dialogs.editCustomer") : t("dialogs.addNewCustomer")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 px-1">
          <div className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label
                  htmlFor="firstName"
                  className={errors.firstName ? "text-red-600 font-medium" : ""}
                >
                  {t("form.fields.firstName")}{" "}
                  <span className="text-red-500">{t("form.fields.required")}</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName")(e.target.value)}
                  placeholder={t("placeholders.enterFirstName")}
                  className={`transition-all duration-200 ${errors.firstName ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
                />
                {errors.firstName && (
                  <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.firstName}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="lastName"
                  className={errors.lastName ? "text-red-600 font-medium" : ""}
                >
                  {t("form.fields.lastName")}{" "}
                  <span className="text-red-500">{t("form.fields.required")}</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName")(e.target.value)}
                  placeholder={t("placeholders.enterLastName")}
                  className={`transition-all duration-200 ${errors.lastName ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
                />
                {errors.lastName && (
                  <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{errors.lastName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Fields */}
            <div className="space-y-2">
              <Label htmlFor="email" className={errors.email ? "text-red-600 font-medium" : ""}>
                {t("form.fields.emailAddress")}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email")(e.target.value)}
                placeholder={t("placeholders.enterEmail")}
                className={`transition-all duration-200 ${errors.email ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
              />
              {errors.email && (
                <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errors.email}</span>
                </div>
              )}
            </div>

            <SimplePhoneInput
              phones={formData.phones}
              onChange={handlePhonesChange}
              errors={errors}
            />

            <div className="space-y-2">
              <Label
                htmlFor="dateOfBirth"
                className={errors.dateOfBirth ? "text-red-600 font-medium" : ""}
              >
                {t("form.fields.dateOfBirth")}
              </Label>
              <Input
                id="dateOfBirth"
                type="text"
                value={dateOfBirthDisplay}
                onChange={(e) => handleDateOfBirthChange(e.target.value)}
                placeholder={t("form.placeholders.dateOfBirthFormat")}
                className={`transition-all duration-200 ${errors.dateOfBirth ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
              />
              {errors.dateOfBirth && (
                <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{errors.dateOfBirth}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t("form.fields.notes")}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange("notes")(e.target.value)}
                placeholder={t("form.placeholders.addNotes")}
                rows={3}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-md border">
                <Switch
                  id="marketingConsent"
                  checked={formData.marketingConsent}
                  onCheckedChange={(checked) => handleChange("marketingConsent")(checked)}
                />
                <div className="space-y-1">
                  <Label htmlFor="marketingConsent" className="text-sm font-medium">
                    {t("form.fields.marketingCommunications")}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t("form.marketing.consentDescription")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md border-l-4 border-l-red-500">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 font-medium">{errors.submit}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            {t("dialogs.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? t("dialogs.updateCustomer") : t("dialogs.createCustomer")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
